import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import React from 'react';
import { hotkeys } from 'react-keyboard-shortcuts';
import CreateButton from '../../core/CreateButton';
import DialogMultiActions from '../../core/DialogMultiActions';
import Errored from '../../core/Errored';
import ListDisplay from '../../core/ListDisplay';
import PrimaryAddButton from '../../core/PrimaryAddButton';
import PrimaryRepeatButton from '../../core/PrimaryRepeatButton';
import TitledTextField from '../../core/TitledTextField';
import { createLengthWarning, makeCancelable, fullySanitiseText, sortObjects } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import { globalStyles as styles } from '../style/gstheme';
import { MAX_USERGROUPNAME_LENGTH } from '../utils/constants';

class CreateGroupDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            acOpen: false,
            checking: false,
            isDuplicate: false,
            label: "",
            isErrored: null
        };
        this.cancellable = null;
        this.textInput = React.createRef();
    }

    hot_keys = {
        'alt+n': {
            priority: 1,
            handler: () => this.handleClickOpen(),
        }
    };

    handleClickOpen = () => this.setState({ open: true });

    findGroup = (value) => {
        this.doAutocomplete(
            {
                search: [{
                    predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                    value: "https://graphifi.com/schema/accesscontrol/UserGroup"
                },
                {
                    predicate: "http://www.w3.org/2000/01/rdf-schema#label",
                    value: value,
                    isExactMatch: false
                }],
                graph: [
                    this.props.graph
                ],
                language: "en",
                predicates: ["http://www.w3.org/2000/01/rdf-schema#label"]
            }
        )
            .promise
            .then((data) => {
                const sortedResults = data.results && data.results.length > 0 ? sortObjects([...data.results], 'label', "en").slice(0, 5) : null;
                this.setState({
                    results: sortedResults,
                    acOpen: data.results && data.results.length > 0 ? true : false
                });
                const isDuplicate = data.results && data.results.length > 0 ? data.results.some(result =>
                    result.label.en.toLowerCase() === this.state.label.toLowerCase()) : false;
                this.setState({ isDuplicate: isDuplicate, checking: false });
            })
            .catch((e) => {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e, checking: false });
            });
    };

    addGroup = () => {
        this.setState({ open: false });
        this.props.onAddGroup(this.state.label);
        this.setState({ label: "" });
    };

    addGroupPlus = () => {
        this.props.onAddGroup(this.state.label);
        this.setState({ label: "" });
        this.textInput && this.textInput.current && this.textInput.current.focus();
    };

    exitAddGroup = () => {
        this.setState({ open: false, label: "", isDuplicate: false });
    };

    handleChange = (event) => {
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
        const t = event.target;
        const cleanText = fullySanitiseText(t.value);
        this.setState({ label: cleanText, checking: true });
        this.hcTimeout = setTimeout(() => {
            if (cleanText.length > 0) {
                this.findGroup(t.value);
            }
            else {
                this.handleClickAway();
            }
        }, 250);
    };

    doAutocomplete(payload) {
        this.cancellable = makeCancelable(GS.autocomplete(payload));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    handleClickAway = () => this.setState({ acOpen: false });

    render() {

        const { label, acOpen, isErrored, isDuplicate, checking } = this.state;
        const { classes } = this.props;

        if (isErrored) return <Errored data={isErrored} />;

        const invalid = label.trim().length === 0 || isDuplicate || checking;

        return (
            <React.Fragment>
                <CreateButton onClick={this.handleClickOpen} label="Create Group" />
                {this.state.open &&
                    <Dialog data-test="create-group" classes={{ paper: classes.paper }} className={classes.dialog} open={this.state.open} onClose={this.exitAddGroup}
                        aria-labelledby="creategroup-dialog-title">
                        <DialogTitle disableTypography className={classes.mainHeading} id="creategroup-dialog-title">Create Group</DialogTitle>
                        <DialogContent style={{ width: '400px' }}>
                            <form autoComplete="off" noValidate="novalidate">
                                <TitledTextField
                                    dataTest="enter-group-name"
                                    name="label"
                                    title="Name"
                                    autoFocus
                                    inputRef={this.textInput}
                                    error={isDuplicate}
                                    required={true}
                                    text={label}
                                    placeholder="Enter group name"
                                    maxLength={MAX_USERGROUPNAME_LENGTH}
                                    helperText={createLengthWarning(label, MAX_USERGROUPNAME_LENGTH)}
                                    onEnter={!isDuplicate && label.trim().length > 0 ? this.addGroup : null}
                                    onCtrlEnter={!isDuplicate && label.trim().length > 0 ? this.addGroupPlus : null}
                                    onChange={this.handleChange} />
                                {acOpen &&
                                    <ListDisplay
                                        handleClickAway={this.handleClickAway}
                                        labelDisplay={(result, lang) => lang ? result.label[lang] : result.label}
                                        lang="en"
                                        title="Existing Groups"
                                        results={this.state.results} />}
                            </form>
                        </DialogContent>
                        <DialogMultiActions onCancel={this.exitAddGroup} >
                            <PrimaryAddButton dataTest="add-group" disabled={invalid} onClick={this.addGroup} />
                            <PrimaryRepeatButton dataTest="add-multiple-groups" disabled={invalid} title="Add group and create another (Ctrl+Enter)" onClick={this.addGroupPlus} />
                        </DialogMultiActions>
                    </Dialog>
                }
            </React.Fragment>
        );
    }
}

CreateGroupDialog.propTypes = {
    classes: PropTypes.object,
    graph: PropTypes.string.isRequired
};

export default withStyles(styles)(hotkeys(CreateGroupDialog));